import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, AlertTriangle, Eye, RefreshCcw, RotateCcw, Truck } from 'lucide-react';

import { AttentionReasons } from '../../../../components/deliveries/AttentionReasons';
import { DataTable } from '../../../../components/ui/DataTable';
import { DropdownMenu } from '../../../../components/ui/DropdownMenu';
import { Button } from '../../../../components/ui/Button';
import { Modal } from '../../../../components/ui/Modal';
import { CopyableText } from '../../../../components/ui/CopyableText';
import { DateRangePicker } from '../../../../components/ui/DateRangePicker';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { STATUS_MAP } from '../../../../utils/catalogs';
import { getChartColor } from '../../../../utils/chartColors';
import { formatRelativeTime } from '../../../../features/dashboard/dashboardUtils';
import { fetchAttention } from '../../../../features/attention/attentionThunks';
import { setFilters } from '../../../../features/attention/attentionSlice';
import {
  ATTENTION_REASON,
  ATTENTION_REASONS,
  ATTENTION_REASON_MAP,
} from '../../../../features/attention/attentionConstants';
import { fetchDeliveryFilterOptions } from '../../../../features/deliveries/deliveriesThunks';
import { COURIER_CHANGE_STATUSES } from '../../../../features/attention/attentionActionThunks';
import { selectHasFulfillmentPro } from '../../../../features/auth/authSelectors';
import { AttentionActions } from './components/AttentionActions';
import { ChangeCourierModal } from './modals/ChangeCourierModal';
import { GetTrackingModal } from './modals/GetTrackingModal';
import { MassiveChangeStatusModal } from '../../../../components/deliveries/MassiveChangeStatusModal';

/**
 * The work queue behind the dashboard's "Necesitan tu atención" card.
 *
 * It exists because two of the four things that card counts — stalled, and
 * without a tracking number — are not filters the deliveries listing knows how
 * to apply, so its numbers had nowhere to lead. Rather than open up that
 * endpoint's SQL, this asks the same question through the same predicates: the
 * card's count and this page's total are the same query, so they cannot drift.
 */
export const DeliveriesAttention = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data, total, isLoading, error, filters } = useSelector((state) => state.attention);
  const { companyOptions } = useSelector((state) => state.deliveries);
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);

  // Arriving from a dashboard card carries its reason in the URL, so the link
  // is shareable and a reload does not drop you back into "Todos".
  const reasonFromUrl = searchParams.get('reason');
  useEffect(() => {
    if (reasonFromUrl && reasonFromUrl !== filters.reason) {
      dispatch(setFilters({ reason: reasonFromUrl }));
    }
    // Only on arrival: after that the chips own the reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasonFromUrl]);

  useEffect(() => {
    dispatch(fetchAttention());
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(fetchDeliveryFilterOptions());
  }, [dispatch]);

  const handleFilterChange = useCallback(
    (patch) => dispatch(setFilters(patch)),
    [dispatch]
  );

  const [selectedRows, setSelectedRows] = useState([]);
  const [openModal, setOpenModal] = useState(null);
  const [singleRow, setSingleRow] = useState(null);

  // The table selects `delivery_id` — which tracking and courier both take —
  // but `change_status` filters by `order__id__in`. The same shipments, two
  // currencies. The pair is remembered as rows go by rather than looked up in
  // the current page, because the selection survives paging and the old rows
  // are gone by then. Same trick as `Deliveries.jsx`.
  const [orderIdByDelivery, setOrderIdByDelivery] = useState({});

  useEffect(() => {
    if (data.length === 0) return;
    setOrderIdByDelivery((prev) => {
      const next = { ...prev };
      data.forEach((row) => { next[row.delivery_id] = row.order_id; });
      return next;
    });
  }, [data]);

  const rowsById = useMemo(() => {
    const map = {};
    data.forEach((row) => { map[row.delivery_id] = row; });
    return map;
  }, [data]);

  // What each action gets: the single row when it came from the row menu, the
  // whole selection when it came from the bar.
  const targetRows = singleRow
    ? [singleRow]
    : selectedRows.map((id) => rowsById[id]).filter(Boolean);
  const targetDeliveryIds = singleRow ? [singleRow.delivery_id] : selectedRows;
  const targetOrderIds = (singleRow ? [singleRow.delivery_id] : selectedRows)
    .map((deliveryId) => orderIdByDelivery[deliveryId])
    .filter((orderId) => orderId !== undefined);

  const closeModal = useCallback(() => {
    setOpenModal(null);
    setSingleRow(null);
  }, []);

  const afterAction = useCallback(() => {
    dispatch(fetchAttention());
    setSelectedRows([]);
  }, [dispatch]);

  const openFor = (modal, row = null) => {
    setSingleRow(row);
    setOpenModal(modal);
  };

  const buildRowActions = (row) => {
    const canChangeCourier = COURIER_CHANGE_STATUSES.includes(row.status);

    return [
      {
        label: 'Ver detalle de la orden',
        icon: Eye,
        onClick: () => navigate(`/order-details/${row.order_id}`),
      },
      // Behind the addon like the bulk one: for two stuck shipments, one by one
      // is the same thing.
      ...(hasFulfillmentPro
        ? [{
          label: 'Cambiar courier',
          icon: Truck,
          onClick: () => openFor('courier', row),
          disabled: !canChangeCourier,
          // A greyed-out row with no reason reads as broken.
          description: canChangeCourier
            ? undefined
            : 'Solo se puede antes de que el envío salga al courier',
        }]
        : []),
      {
        label: 'Obtener tracking',
        icon: RefreshCcw,
        onClick: () => openFor('tracking', row),
      },
      {
        label: 'Cambiar estado',
        icon: Activity,
        onClick: () => openFor('status', row),
        danger: true,
        dividerBefore: true,
        description: 'Afecta a todos los bultos del envío',
      },
      {
        label: 'Generar devolución · Pronto',
        icon: RotateCcw,
        onClick: () => openFor('return', row),
        dividerBefore: true,
      },
    ];
  };

  const current = ATTENTION_REASON_MAP[filters.reason] || ATTENTION_REASON_MAP[ATTENTION_REASON.ALL];
  const companySelectOptions = [{ value: 'all', label: 'Todas' }, ...companyOptions];

  const columns = [
    {
      header: 'Orden',
      accessor: 'order_id',
      cell: (row) => (
        <div className="flex flex-col">
          <Link
            to={`/order-details/${row.order_id}`}
            title="Ver Detalles"
            data-testid="attention-order-link"
            className="font-bold text-ninja-dark dark:text-white hover:text-ninja-green hover:underline transition-colors w-fit"
          >
            #{row.order_id}
          </Link>
          {row.reference_id && (
            <span className="text-xs text-text-muted">{row.reference_id}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Estado',
      accessor: 'status',
      cell: (row) => {
        const color = getChartColor(row.status);
        return (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ backgroundColor: `${color}1a`, color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            {STATUS_MAP[row.status]?.user_name || row.status}
          </span>
        );
      },
    },
    {
      header: 'Motivo',
      accessor: 'reason',
      cell: (row) => <AttentionReasons row={row} />,
    },
    {
      header: 'Último movimiento',
      accessor: 'last_movement',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-text-secondary">
            {row.last_movement ? formatRelativeTime(row.last_movement) : 'Sin historial'}
          </span>
          {!row.last_movement && row.unchanged_since && (
            <span className="text-xs text-text-muted">
              Creado {formatRelativeTime(row.unchanged_since).toLowerCase()}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Destino',
      accessor: 'commune',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-ninja-dark dark:text-white">{row.customer || '-'}</span>
          <span className="text-xs text-text-muted">{row.commune || 'Sin comuna'}</span>
        </div>
      ),
    },
    {
      header: 'Empresa',
      accessor: 'company',
      cell: (row) => <span className="text-sm text-text-secondary">{row.company || '-'}</span>,
    },
    {
      header: 'Tracking',
      accessor: 'tracking_number',
      cell: (row) =>
        row.tracking_number
          ? <CopyableText value={row.tracking_number} maxWidth="140px" />
          : <span className="text-xs text-text-muted">Sin número</span>,
    },
    {
      header: '',
      cell: (row) => <DropdownMenu items={buildRowActions(row)} />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Same running order as Envíos: chips, then filters, then the options
          row above the table. No page title — the header already carries it for
          this section, the way `/deliveries` does. */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          {ATTENTION_REASONS.map((reason) => {
            const isActive = filters.reason === reason.value;
            return (
              <button
                key={reason.value}
                onClick={() => handleFilterChange({ reason: reason.value })}
                className={clsx(
                  'px-4 py-2 rounded-full text-sm font-semibold border transition-all',
                  isActive
                    ? 'bg-ninja-green text-white border-ninja-green shadow-sm'
                    : 'bg-white dark:bg-[#1a1a1a] text-text-secondary border-border hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]'
                )}
              >
                {reason.label}
              </button>
            );
          })}
        </div>

        {/* Right under the chips because it explains the one that is selected:
            "estancado" is a threshold somebody chose, and the screen should say
            which. */}
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <AlertTriangle size={15} className="text-warning flex-shrink-0" />
          {current.description}
        </div>
      </div>

      <div className="flex flex-col md:flex-row flex-wrap items-center gap-4">
        <DateRangePicker
          startDate={filters.start_date}
          endDate={filters.end_date}
          onChange={handleFilterChange}
        />

        <FilterableSelect
          label="Empresa:"
          value={filters.company}
          onChange={(value) => handleFilterChange({ company: value })}
          options={companySelectOptions}
        />
      </div>

      <AttentionActions
        selectedCount={selectedRows.length}
        onChangeStatus={() => openFor('status')}
        onGetTracking={() => openFor('tracking')}
        onChangeCourier={() => openFor('courier')}
      />

      <DataTable
        columns={columns}
        data={data}
        totalItems={total}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={isLoading}
        onPageChange={(page) => handleFilterChange({ page })}
        onRowsPerPageChange={(rowsPerPage) => handleFilterChange({ rowsPerPage })}
        idAccessor="delivery_id"
        itemLabel="envíos"
        error={error}
        onRetry={() => dispatch(fetchAttention())}
        emptyMessage="Nada que atender en este periodo. Todo se está moviendo."
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
      />

      <MassiveChangeStatusModal
        isOpen={openModal === 'status'}
        onClose={closeModal}
        selectedOrderIds={targetOrderIds}
        onSuccess={afterAction}
      />

      <GetTrackingModal
        isOpen={openModal === 'tracking'}
        onClose={closeModal}
        deliveryIds={targetDeliveryIds}
        onSuccess={afterAction}
      />

      <ChangeCourierModal
        isOpen={openModal === 'courier'}
        onClose={closeModal}
        rows={targetRows}
        onSuccess={afterAction}
      />

      {/* Nothing to fill in yet: there is no endpoint, no model and no task
          behind this. A form that cannot save would promise more than exists. */}
      <Modal isOpen={openModal === 'return'} onClose={closeModal} title="Generar devolución">
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="w-14 h-14 rounded-2xl bg-ninja-greenLight text-ninja-green flex items-center justify-center">
            <RotateCcw size={26} />
          </div>
          <p className="text-sm text-text-secondary max-w-sm">
            Todavía estamos construyendo la devolución automática. Por ahora, para devolver un envío
            cámbiale el estado a <strong className="text-ninja-dark dark:text-white">En devolución</strong>.
          </p>
          <Button variant="primary" pill onClick={closeModal}>
            Entendido
          </Button>
        </div>
      </Modal>
    </div>
  );
};
